import type { Board } from "../board";
import { PRIORITY_LAST, type SomethingWithState, type OnAddToBoard, type ProcessableThing } from "../interfaces";
import { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import { OrganicMobCorpse } from "../mobs/organicMobCorpse";
import { Floor } from "./floors/floor";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";

export enum WaterTileState {
	TOP = "TOP",
	NORMAL = "NORMAL",
}

export class WaterTile extends PhysicalThing implements SomethingWithState, OnAddToBoard, ProcessableThing {
	static key = "WaterTile";
	private state: WaterTileState = WaterTileState.TOP;

	constructor(xy: XY) {
		super(xy, false);
	}

	getZOrder(): number {
		return ZOrder.CARPET_LIKE;
	}

	onAddToBoard(): void {
		const northXY = this.xy.add(0, -1);
		if (this.board!.isInBounds(northXY)) {
			const hasWater = this.board!.getThingsAt(northXY).some((t) => t instanceof WaterTile);
			this.state = hasWater ? WaterTileState.NORMAL : WaterTileState.TOP;
		} else {
			this.state = WaterTileState.TOP;
		}
	}

	processTurn(board: Board): void {
		const things = board.getThingsAt(this.xy);
		// A bridge (Floor) over the water holds the corpse up instead of sinking it.
		if (things.some((t) => t instanceof Floor)) return;
		for (const thing of things) {
			if (thing instanceof OrganicMobCorpse && thing.active) {
				board.removeAtEndOfTurn(thing);
			}
		}
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	getState(): WaterTileState {
		return this.state;
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), state: this.state };
	}

	static deserialize(data: any): WaterTile {
		const w = new WaterTile(deserializeXY(data.xy));
		w.visualId = data.visualId;
		w.state = data.state ?? WaterTileState.TOP;
		return w;
	}
}
